/**
 * Runtime-manifest context (Phase 5 §3) — ONE runtime-manifest fetch per shell,
 * shared by everything that needs it.
 *
 * Two consumers need the same answer: the generated route table (to mount a
 * live-authored page's route) and the app shell's sidebar (to show its nav
 * entry). The DoD requires both — a page that routes but never appears in the
 * nav is only half-served. Letting each call `useRuntimeManifests`
 * independently would mean two hook instances, two cold-start requests, and two
 * copies of state that can disagree mid-flight; a provider gives them one
 * fetch, one list and one loading state.
 *
 * Fetch failure is not an error state for the shell: `useRuntimeManifests`
 * resolves to empty arrays when the manifest endpoints are unreachable, so the
 * app falls back to exactly the static build-time pages it had before §3.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ManifestClient, RuntimeNavEntry } from './manifest-client.js';
import { useRuntimeManifests } from './useRuntimeManifests.js';

export interface RuntimeManifestContextValue {
  /** The shared client, or null when runtime fetching is off (no auth yet). */
  readonly client: ManifestClient | null;
  /** Nav entries for live-authored pages, already role-filtered. */
  readonly navEntries: readonly RuntimeNavEntry[];
  /** True while the first fetch is in flight. */
  readonly loading: boolean;
  /** Non-null when the fetch failed — the shell still renders its static pages. */
  readonly error: Error | null;
}

/** Inert default: no client, no runtime pages — exactly the pre-§3 behaviour. */
const EMPTY: RuntimeManifestContextValue = {
  client: null,
  navEntries: [],
  loading: false,
  error: null,
};

const RuntimeManifestContext = createContext<RuntimeManifestContextValue>(EMPTY);

export interface RuntimeManifestProviderProps {
  /** Created once at app boot; null disables runtime fetching entirely. */
  readonly client: ManifestClient | null;
  /** Which shell is asking — the server scopes its answer to this. */
  readonly app?: string;
  /** The signed-in user's roles, for the client-side guard. */
  readonly userRoles?: readonly string[] | undefined;
  /** Whether the user is a platform admin (bypasses role guards). */
  readonly isAdmin?: boolean;
  readonly children: ReactNode;
}

/**
 * Provide the runtime manifests to the shell. Mount ONCE, inside the auth
 * provider (the client needs a token) and above both the route table and the
 * layout.
 *
 * The role props filter what this browser renders; they are not the security
 * boundary. The server withholds a guarded page's manifest from a caller
 * without the role, so an un-entitled page never reaches the client to be
 * filtered in the first place.
 */
export function RuntimeManifestProvider({
  client,
  app = 'frontend',
  userRoles,
  isAdmin = false,
  children,
}: RuntimeManifestProviderProps) {
  const { navEntries, loading, error } = useRuntimeManifests({
    client,
    app,
    userRoles,
    isAdmin,
    enabled: client != null,
  });

  const value = useMemo<RuntimeManifestContextValue>(
    () => ({ client, navEntries, loading, error }),
    [client, navEntries, loading, error],
  );

  return <RuntimeManifestContext.Provider value={value}>{children}</RuntimeManifestContext.Provider>;
}

/**
 * Read the shared runtime manifests. Safe to call outside the provider — it
 * yields the inert default, so a surface that has not been wired up simply
 * shows its static pages.
 */
export function useRuntimeManifestContext(): RuntimeManifestContextValue {
  return useContext(RuntimeManifestContext);
}
